import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { avatars, scenes } from '@/config/siteImages';

export default function Hero() {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#FCFAFD]">
      {/* Subtle brand gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#F8F3FB] via-[#FCFAFD] to-white pointer-events-none" />
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#F3EBF8]/60 to-transparent pointer-events-none" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pt-28 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">

          {/* Left Content */}
          <div className="lg:col-span-6">
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-2 mb-8">
              {[
                { icon: 'ri-award-line', text: t('hero_badge_experience') },
                { icon: 'ri-group-line', text: t('hero_badge_mentors') },
                { icon: 'ri-shield-check-line', text: t('hero_badge_qualified') },
              ].map(({ icon, text }) => (
                <div
                  key={text}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#E9D9F2] bg-white"
                >
                  <i className={`${icon} text-[#4F2D63] text-xs`}></i>
                  <span className="text-xs font-medium text-[#4F2D63] whitespace-nowrap">{text}</span>
                </div>
              ))}
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl lg:text-[4rem] font-bold text-gray-900 leading-[1.1] mb-5 tracking-tight">
              <span className="block">{t('hero_headline_1')}</span>
              <span className="block text-[#4F2D63]">{t('hero_headline_2')}</span>
              <span className="block">{t('hero_headline_3')}</span>
            </h1>

            {/* Subheadline */}
            <p className="text-base text-gray-500 leading-relaxed mb-3 max-w-lg">
              {t('hero_subheadline')}
            </p>

            {/* Compliance */}
            <p className="text-xs text-gray-400 italic mb-8">
              {t('hero_compliance')}
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-3 mb-10">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-sm transition-all duration-200 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap"
              >
                {t('hero_cta_primary')}
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl border border-[#E9D9F2] hover:border-[#4F2D63] bg-white text-gray-700 hover:text-[#4F2D63] font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line"></i>
                {t('hero_cta_secondary')}
              </Link>
            </div>

            {/* Social Proof Row */}
            <div className="flex items-center gap-4">
              <div className="flex -space-x-2">
                {[
                  avatars.small.latino,
                  avatars.small.blackWoman,
                  avatars.small.asianMan,
                  avatars.small.whiteMan,
                  avatars.small.latina,
                ].map((src, i) => (
                  <div key={i} className="w-9 h-9 rounded-full border-2 border-white overflow-hidden flex-shrink-0">
                    <img src={src} alt={`Business owner ${i + 1}`} className="w-full h-full object-cover object-top" />
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 mb-0.5">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>
                  ))}
                </div>
                <p className="text-xs text-gray-500">{t('hero_social_proof')}</p>
              </div>
            </div>
          </div>

          {/* Right: Photo + Stats Card */}
          <div className="lg:col-span-6 flex items-center justify-center lg:justify-end">
            <div className="relative w-full max-w-md">
              {/* Main photo */}
              <div className="rounded-2xl overflow-hidden border border-[#E9D9F2] w-full h-80 lg:h-96">
                <img
                  src={scenes.heroEntrepreneur}
                  alt="Business owner accessing funding"
                  className="w-full h-full object-cover object-top"
                />
              </div>

              {/* Stats card floating */}
              <div className="absolute -bottom-6 -left-6 bg-white border border-[#E9D9F2] rounded-xl px-5 py-4 shadow-md">
                <div className="flex flex-col gap-3">
                  {[
                    { value: t('hero_stat_funded'), label: 'Total Funding Guided', icon: 'ri-funds-line' },
                    { value: t('hero_stat_businesses'), label: 'Business Owners', icon: 'ri-store-2-line' },
                  ].map(({ value, label, icon }) => (
                    <div key={label} className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#F8F3FB] flex-shrink-0">
                        <i className={`${icon} text-[#4F2D63] text-sm`}></i>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900 leading-none">{value}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{label}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fast review badge */}
              <div className="absolute -top-4 -right-4 bg-[#4F2D63] rounded-xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <i className="ri-time-line text-[#DBB6EE] text-sm"></i>
                  <div>
                    <p className="text-xs font-bold text-white">Fast Review</p>
                    <p className="text-xs text-[#DBB6EE]">48-hr turnaround</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Strip */}
      <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-[#E9D9F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3.5">
          <div className="flex flex-wrap items-center justify-center gap-6 lg:gap-12">
            {[
              { icon: 'ri-money-dollar-circle-line', text: t('trust_no_hidden_fees') },
              { icon: 'ri-flashlight-line', text: t('trust_fast_process') },
              { icon: 'ri-customer-service-2-line', text: t('trust_expert_support') },
              { icon: 'ri-file-list-3-line', text: t('trust_transparent') },
              { icon: 'ri-lock-2-line', text: t('trust_secure') },
            ].map(({ icon, text }) => (
              <div key={text} className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className={`${icon} text-[#4F2D63] text-sm`}></i>
                </div>
                <span className="text-xs font-medium text-gray-500 whitespace-nowrap">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
